import { Tab, Tabs } from "@mui/material";
import { SegmentedTabs } from "@zfp-akademie-a-s/zfp-design";
import { Resources, useResource } from "Infrastructure/Translations/Resources";

enum FirstLevelMenuItem {
  ClientAnalysis = 0,
  FinancialAnalysis = 1,
  ClientResult = 2,
}

export enum AnalysisDetailSection {
  Family = 0,
  Goals = 1,
  InvestmentQuestionnaire = 2,
  Budget = 3,
  Assets = 4,
  Insurance = 5,
  GoalFulfillment = 6,
  AssetsAndContracts = 7,
  Modeling = 8,
  ResultDocuments = 9,
}

const CLIENT_ANALYSIS_SECTIONS: AnalysisDetailSection[] = [
  AnalysisDetailSection.Family,
  AnalysisDetailSection.Goals,
  AnalysisDetailSection.InvestmentQuestionnaire,
  AnalysisDetailSection.Budget,
  AnalysisDetailSection.Assets,
  AnalysisDetailSection.Modeling,
];

const FINANCIAL_ANALYSIS_SECTIONS: AnalysisDetailSection[] = [
  AnalysisDetailSection.GoalFulfillment,
  AnalysisDetailSection.Insurance,
  AnalysisDetailSection.AssetsAndContracts,
];

const CLIENT_RESULT_SECTIONS: AnalysisDetailSection[] = [AnalysisDetailSection.ResultDocuments];

type Props = {
  readonly value: AnalysisDetailSection;
  readonly onChange: (value: AnalysisDetailSection) => void;
};

const getFirstLevelBySection = (section: AnalysisDetailSection) => {
  if (CLIENT_ANALYSIS_SECTIONS.includes(section)) return FirstLevelMenuItem.ClientAnalysis;
  if (FINANCIAL_ANALYSIS_SECTIONS.includes(section)) return FirstLevelMenuItem.FinancialAnalysis;
  if (CLIENT_RESULT_SECTIONS.includes(section)) return FirstLevelMenuItem.ClientResult;
  return FirstLevelMenuItem.ClientAnalysis;
};

const getInitSecondLevelTab = (firstLevelValue: FirstLevelMenuItem) => {
  switch (firstLevelValue) {
    case FirstLevelMenuItem.ClientAnalysis:
      return AnalysisDetailSection.Family;
    case FirstLevelMenuItem.FinancialAnalysis:
      return AnalysisDetailSection.GoalFulfillment;
    case FirstLevelMenuItem.ClientResult:
      return AnalysisDetailSection.ResultDocuments;
  }
};

// The analysis's part switch (Analýza klienta / Finanční analýza / Výstup pro klienta), above the
// section tabs; switching opens the part's first section
export const AnalysisPartSwitch: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useResource();

  return (
    <SegmentedTabs
      value={getFirstLevelBySection(value)}
      onChange={(_, newFirstLevel: FirstLevelMenuItem) => onChange(getInitSecondLevelTab(newFirstLevel))}
    >
      <Tab label={t(Resources.AnalysisDetail.Tab.ClientAnalysis)} value={FirstLevelMenuItem.ClientAnalysis} />
      <Tab label={t(Resources.AnalysisDetail.Tab.FinancialAnalysis)} value={FirstLevelMenuItem.FinancialAnalysis} />
      <Tab label={t(Resources.AnalysisDetail.Tab.ClientResult)} value={FirstLevelMenuItem.ClientResult} />
    </SegmentedTabs>
  );
};

// The sections of the part the switch has open (Rodina, Cíle, …), above the content
export const AnalysisSectionTabs: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useResource();
  const firstLevelValue = getFirstLevelBySection(value);

  const clientAnalysisTabs = [
    {
      label: t(Resources.AnalysisDetail.TabMenu.Family),
      value: AnalysisDetailSection.Family,
    },
    {
      label: t(Resources.AnalysisDetail.TabMenu.Goals),
      value: AnalysisDetailSection.Goals,
    },
    {
      label: t(Resources.AnalysisDetail.TabMenu.InvestmentQuestionnaire),
      value: AnalysisDetailSection.InvestmentQuestionnaire,
    },
    {
      label: t(Resources.AnalysisDetail.TabMenu.Budget),
      value: AnalysisDetailSection.Budget,
    },
    {
      label: t(Resources.AnalysisDetail.TabMenu.Assets),
      value: AnalysisDetailSection.Assets,
    },
    {
      label: t(Resources.AnalysisDetail.TabMenu.Modeling),
      value: AnalysisDetailSection.Modeling,
    },
  ];

  const financialAnalysisTabs = [
    {
      label: t(Resources.AnalysisDetail.TabMenu.GoalsFulfillment),
      value: AnalysisDetailSection.GoalFulfillment,
    },
    {
      label: t(Resources.AnalysisDetail.TabMenu.Insurance),
      value: AnalysisDetailSection.Insurance,
    },
    {
      label: t(Resources.AnalysisDetail.TabMenu.WealthAndAgreements),
      value: AnalysisDetailSection.AssetsAndContracts,
    },
  ];

  const clientResultTabs = [
    {
      label: t(Resources.AnalysisDetail.TabMenu.ResultDocuments),
      value: AnalysisDetailSection.ResultDocuments,
    },
  ];

  return (
    <Tabs value={value} onChange={(_, newSection: AnalysisDetailSection) => onChange(newSection)}>
      {firstLevelValue === FirstLevelMenuItem.ClientAnalysis &&
        clientAnalysisTabs.map((tab) => <Tab key={tab.value} label={tab.label} value={tab.value} />)}
      {firstLevelValue === FirstLevelMenuItem.FinancialAnalysis &&
        financialAnalysisTabs.map((tab) => <Tab key={tab.value} label={tab.label} value={tab.value} />)}
      {firstLevelValue === FirstLevelMenuItem.ClientResult &&
        clientResultTabs.map((tab) => <Tab key={tab.value} label={tab.label} value={tab.value} />)}
    </Tabs>
  );
};
